import { produce } from 'immer';
import { renderToString } from 'react-dom/server';
import { getUnitValue, units } from '@ui/css';
import { isPlatformSafari, getAbsoluteNodeOffset } from '@utils/common';
import { pageSelectors } from './comp.page.style.js';
import { Slot } from './comp.slot.js';
import { jsx } from 'react/jsx-runtime';

const AVOID_BREAK_INSIDE_SELECTOR = "audio, video, iframe, img, *[data-avoid-break-inside='force'], *[data-avoid-break-inside='true'], *[data-avoid-break-after='true']";
const MAX_ASSUMED_LINE_HEIGHT = 150;
function calculateDimensions(args) {
  const physicalPages = [];
  const chunkDimensions = new Map();
  const pageContent = args.templatePage.querySelector(pageSelectors.pageContent());
  if (!pageContent) {
    throw new Error("Page.calculateDimensions(): Cannot resolve content to extract dimensions from.");
  }
  let templateHeight = pageContent.scrollHeight;
  const sizeOfOnePixel = getUnitValue(units.px(100), args.templatePage) / 100;
  const physicalPageHeightInPx = getUnitValue(units.vh(100), args.templatePage) - (isPlatformSafari() && args.target === "browser" ? 40 : 0);
  let currentPageOffset = 0;
  let pageIndex = 0;
  const chunks = [...args.templatePage.querySelectorAll(pageSelectors.chunk())];
  let contentRects = getContentRects(pageContent, physicalPageHeightInPx, sizeOfOnePixel);
  while (currentPageOffset < templateHeight) {
    const physicalPageContentHeightInPx = getPhysicalPageContentHeightInPx(args, physicalPageHeightInPx, pageIndex);
    const stateForPage = (() => {
      const existing = physicalPages[pageIndex];
      if (existing) {
        return existing;
      }
      const pageState = {
        translateYInPx: currentPageOffset * -1,
        heightInPx: physicalPageContentHeightInPx
      };
      physicalPages.push(pageState);
      return pageState;
    })();
    let willCropAt = currentPageOffset + physicalPageContentHeightInPx;
    const croppedChunkIndex = chunks.findIndex(it => it.offsetTop + it.offsetHeight > willCropAt);
    const croppedChunk = chunks[croppedChunkIndex];
    const columnsInCroppedChunk = [...(croppedChunk?.querySelectorAll(pageSelectors.column()) ?? [])];

    // In case the chunk that will be cropped on the current page includes
    // multiple columns, then the crop handling must be performed on a
    // per-column basis.
    if (croppedChunk && columnsInCroppedChunk.length > 1) {
      const newHeightOffset = determineChunkColumnsCutoffOnPage({
        state: chunkDimensions,
        pageIndex,
        physicalPageHeightInPx,
        willCropPageAt: willCropAt,
        chunkIndex: croppedChunkIndex,
        templateChunk: croppedChunk,
        templateColumnsInChunk: columnsInCroppedChunk,
        sizeOfOnePixel
      });
      templateHeight += newHeightOffset;
      contentRects = getContentRects(pageContent, physicalPageHeightInPx, sizeOfOnePixel);
    } else {
      // ... however, for single column chunks we can simply determine any
      // content that will be cropped by the current cutoff and translate
      // accordingly.
      const relevantRects = contentRects.filter(it => it.top < willCropAt);
      const shouldCropAtToAvoidClippingContent = determineCropPointToAvoidClipping(willCropAt, relevantRects, physicalPageHeightInPx, willCropAt - physicalPageHeightInPx * 0.5);

      //
      // In case we can determine a better cropping point, which avoids content
      // being cut inside, then apply this instead of naively filling the page
      // fully.
      //
      if (shouldCropAtToAvoidClippingContent !== willCropAt) {
        stateForPage.heightInPx = physicalPageContentHeightInPx - (willCropAt - shouldCropAtToAvoidClippingContent);
        willCropAt = shouldCropAtToAvoidClippingContent;
      }
    }
    currentPageOffset = willCropAt;
    pageIndex++;
  }
  return {
    pages: physicalPages,
    chunks: chunkDimensions
  };
}
/**
 * Handling cutoff inside chunks with multiple columns adds an additional set
 * of challenges as we must be able to ensure that content in no column will be
 * cropped badly.
 *
 * NB: By avoiding cutoffs inside chunks with columns, then the height of the
 * column may become larger than before the operation and any consumers should
 * account for this.
 */
function determineChunkColumnsCutoffOnPage(args) {
  const initialChunkHeight = args.templateChunk.getBoundingClientRect().height;
  const chunkState = args.state.get(args.chunkIndex) ?? {
    chunkHeightInPx: args.templateChunk.getBoundingClientRect().height ?? 0,
    onPage: {}
  };
  for (let columnIndex = 0; columnIndex < args.templateColumnsInChunk.length; columnIndex++) {
    const columnInChunk = args.templateColumnsInChunk[columnIndex];
    if (!columnInChunk) {
      continue;
    }
    const offsetDueToTranslation = chunkState.onPage[args.pageIndex]?.columns[columnIndex]?.translateYInPx ?? 0;
    const willCropColumnAt = args.willCropPageAt - offsetDueToTranslation;
    const contentRects = getContentRects(columnInChunk, args.physicalPageHeightInPx, args.sizeOfOnePixel);
    const relevantRects = contentRects.filter(it => it.top < willCropColumnAt);
    const shouldCropAtToAvoidClippingColumn = determineCropPointToAvoidClipping(willCropColumnAt, relevantRects, args.physicalPageHeightInPx, willCropColumnAt - 0.5 * args.physicalPageHeightInPx);

    //
    // In case we can determine a better cropping point, which avoids content
    // iknside a column being cut, then apply this instead of naively filling
    // the page with the column.
    //
    if (willCropColumnAt !== shouldCropAtToAvoidClippingColumn) {
      const heightThatAvoidsCropping = shouldCropAtToAvoidClippingColumn - columnInChunk.getBoundingClientRect().top;

      // As we've currently reduced the height of the column on the current
      // page, then this overflowing content should be present on the next page.
      // We achieve this by translating the chunk column downwards on the next
      // page.
      const translateOffsetOnNextPage = willCropColumnAt - shouldCropAtToAvoidClippingColumn;

      // ... and since we've translated content downwards, then the column have
      // effectively become larger by this translation.
      columnInChunk.style.height = `${columnInChunk.getBoundingClientRect().height + translateOffsetOnNextPage}px`;
      chunkState.onPage[args.pageIndex] = produce(state => {
        state.columns[columnIndex] ??= {};
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state.columns[columnIndex].heightInPx = heightThatAvoidsCropping;
        return state;
      })(chunkState.onPage[args.pageIndex] ?? {
        columns: []
      });
      chunkState.onPage[args.pageIndex + 1] = produce(state => {
        state ??= {
          columns: []
        };
        state.columns[columnIndex] ??= {};
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state.columns[columnIndex].translateYInPx = translateOffsetOnNextPage + offsetDueToTranslation;
        return state;
      })(chunkState.onPage[args.pageIndex + 1] ?? {
        columns: []
      });
    }
  }
  const newChunkHeight = Math.max(...args.templateColumnsInChunk.map(it => it.getBoundingClientRect().height));
  chunkState.chunkHeightInPx = newChunkHeight;
  args.state.set(args.chunkIndex, chunkState);
  args.templateChunk.style.height = `${chunkState.chunkHeightInPx}px`;
  return newChunkHeight - initialChunkHeight;
}
function determineCropPointToAvoidClipping(currentCutoffPoint, contentRects, physicalPageContentHeightInPx, minPossibleCutInPx) {
  const intersectingRects = contentRects.filter(rect => rect.top < currentCutoffPoint - 0.5 && rect.bottom > currentCutoffPoint + 0.5);
  if (intersectingRects.length === 0) {
    return currentCutoffPoint;
  }
  const shouldCropAtToAvoidClippingContent = Math.max(minPossibleCutInPx, Math.min(...intersectingRects.map(it => it.top), currentCutoffPoint));
  if (shouldCropAtToAvoidClippingContent === currentCutoffPoint || shouldCropAtToAvoidClippingContent === minPossibleCutInPx) {
    return shouldCropAtToAvoidClippingContent;
  } else {
    return determineCropPointToAvoidClipping(shouldCropAtToAvoidClippingContent, contentRects, physicalPageContentHeightInPx, minPossibleCutInPx);
  }
}
function getPhysicalPageContentHeightInPx(args, physicalPageHeightInPx, pageIndex) {
  const bestEffortContext = {
    virtual: args.currentContext.virtual,
    physical: {
      ...args.currentContext.virtual,
      currentPageIndexInSection: args.currentContext.virtual.currentPageIndexInSection + pageIndex
    }
  };
  const headerHeightInPx = getSlotHeight(args.templatePage, bestEffortContext, args.slots?.header);
  const footerHeightInPx = getSlotHeight(args.templatePage, bestEffortContext, args.slots?.footer);
  const padding = args.padding?.({
    ...bestEffortContext,
    hasHeaderOnPage: headerHeightInPx > 0,
    hasFooterOnPage: footerHeightInPx > 0
  });
  const paddingTopInPx = padding?.top ? getUnitValue(padding.top, args.templatePage) : 0;
  const paddingBottomInPx = padding?.bottom ? getUnitValue(padding.bottom, args.templatePage) : 0;
  return physicalPageHeightInPx - headerHeightInPx - footerHeightInPx - paddingTopInPx - paddingBottomInPx;
}
function getContentRects(templateColumn, physicalPageContentHeightInPx, sizeOfOnePixel) {
  const range = document.createRange();
  range.selectNodeContents(templateColumn);
  const lineRects = [...range.getClientRects()].map(line => ({
    // NB: We shift the bounding-box slightly to be absolutely sure
    // that subpixel offsets cannot end in lines overflowing ever so
    // slightly to the next page.
    top: line.top,
    bottom: line.bottom,
    height: line.height
  })).filter(rect => rect.height >= 15 && rect.height < MAX_ASSUMED_LINE_HEIGHT);
  const snappoints = [...templateColumn.querySelectorAll("*[data-avoid-break-snap-point='true']")].map(it => {
    const offset = getAbsoluteNodeOffset(it, templateColumn);
    const extendTopBy = it.dataset["extendTopBy"] ? Number(it.dataset["extendTopBy"]) * sizeOfOnePixel : 0;
    const extendBottomBy = it.dataset["extendBottomBy"] ? Number(it.dataset["extendBottomBy"]) * sizeOfOnePixel : 0;
    return {
      // NB: We extend the bounding-box slightly to be absolutely sure that
      // subpixel offsets cannot end in content overflowing ever so slightly
      // to the next page.
      top: offset.y - extendTopBy,
      bottom: offset.y + it.clientHeight + extendBottomBy,
      height: it.clientHeight + extendBottomBy + extendTopBy
    };
  });
  const avoidBreakInsideRects = [...templateColumn.querySelectorAll(AVOID_BREAK_INSIDE_SELECTOR)].map(it => {
    const offset = getAbsoluteNodeOffset(it, templateColumn);
    const extendTopBy = it.dataset["extendTopBy"] ? Number(it.dataset["extendTopBy"]) * sizeOfOnePixel : 0;
    const extendBottomBy = it.dataset["extendBottomBy"] ? Number(it.dataset["extendBottomBy"]) * sizeOfOnePixel : 0;
    return {
      __avoidBreakAfter: it.dataset["avoidBreakAfter"] === "true",
      __force: it.dataset["avoidBreakInside"] === "force",
      // NB: We extend the bounding-box slightly to be absolutely sure that
      // subpixel offsets cannot end in content overflowing ever so slightly
      // to the next page.
      top: offset.y - extendTopBy - 1,
      bottom: offset.y + it.clientHeight + extendBottomBy + 1,
      height: it.clientHeight + extendBottomBy + extendTopBy + 2
    };
  })
  //
  // NB: In case nodes are exessively large compared to a physical page, then
  // we have no choice but to crop them at some point, and as such they are
  // excluded from out content rects.
  //
  .filter(rect => rect.height < physicalPageContentHeightInPx * 0.5 || rect.__force);
  return [...lineRects, ...avoidBreakInsideRects.map(rect => {
    if (!rect.__avoidBreakAfter) {
      return rect;
    }
    const nextLineRect = lineRects.find(line => line.top > rect.bottom && line.top > rect.bottom);
    const nextElementRect = avoidBreakInsideRects.find(element => element.top > rect.bottom && element.top > rect.bottom);
    const nextSnappointRect = snappoints.find(element => element.top > rect.bottom && element.top > rect.bottom);
    const rectToMergeWith = [nextLineRect, nextElementRect, nextSnappointRect].sort((a, b) => (a?.bottom ?? Infinity) - (b?.bottom ?? Infinity))[0];
    if (!rectToMergeWith) {
      return rect;
    }
    const extendedRect = {
      top: rect.top,
      bottom: rectToMergeWith.bottom,
      height: rectToMergeWith.bottom - rect.top
    };
    if (extendedRect.height >= physicalPageContentHeightInPx * 0.5) {
      return rect;
    }
    return extendedRect;
  })];
}
function getSlotHeight(templatePage, context, slotGenerator) {
  if (!slotGenerator) {
    return 0;
  }
  const node = renderToString(/*#__PURE__*/jsx(Slot, {
    context: context,
    slot: slotGenerator
  }));
  const container = document.createElement("div");
  container.innerHTML = node;
  templatePage.appendChild(container);
  const height = container.getBoundingClientRect().height;
  container.remove();
  return height;
}

export { calculateDimensions };
//# sourceMappingURL=util.calculate-physical-page-dimensions.js.map
